import { useMemo } from 'react';
import { CareSubscriptionTrueUpType, useGetCareEnrollmentTrueUpHistoryQuery } from 'shared/types/graphql';
import { centsToDollars } from '../utils/currency';

interface UseTrueUpInfoInput {
  carePlanEnrollmentId?: string;
}

interface TrueUpInfo {
  remainingBalance: number;
  nextBillingDate: string;
  finalPaymentDate: string;
  isRetrying: boolean;
}

interface UseTrueUpInfoOutput {
  trueUpData?: TrueUpInfo;
  refetchTrueUpData: () => Promise<void>;
}

export const useTrueUpInfo = ({ carePlanEnrollmentId }: UseTrueUpInfoInput): UseTrueUpInfoOutput => {
  const { data, loading, refetch } = useGetCareEnrollmentTrueUpHistoryQuery({
    variables: {
      where: {
        carePlanEnrollmentId: { equals: carePlanEnrollmentId },
        type: { equals: CareSubscriptionTrueUpType.Internal },
      },
    },
    skip: !carePlanEnrollmentId,
  });

  const trueUpData = data?.findManyCareSubscriptionTrueUp;

  const nonCompletedPayments = useMemo(() => trueUpData?.filter((item) => !item.completedAt) || [], [trueUpData]);

  const remainingBalance = useMemo(() => {
    const balanceInCents = nonCompletedPayments.reduce((prev, curr) => curr.amount + prev, 0) || 0;
    return centsToDollars(balanceInCents);
  }, [nonCompletedPayments]);

  const nextPayment = useMemo(() => trueUpData?.find((item) => !item.completedAt), [trueUpData]);

  const nextBillingDate = useMemo(() => nextPayment?.dueAt || '', [nextPayment?.dueAt]);

  const finalPaymentDate = useMemo(() => {
    if (!trueUpData?.length) {
      return '';
    }

    const lastTrueUp = trueUpData[trueUpData.length - 1];
    return lastTrueUp.dueAt;
  }, [trueUpData]);

  const isRetrying = useMemo(
    () => nonCompletedPayments.some((payment) => !!payment.retryAt && !payment.completedAt),
    [nonCompletedPayments],
  );

  const shouldReturnData = !!carePlanEnrollmentId && !loading && !!trueUpData?.length;

  return {
    trueUpData: shouldReturnData
      ? {
          remainingBalance,
          nextBillingDate,
          finalPaymentDate,
          isRetrying,
        }
      : undefined,
    refetchTrueUpData: async (): Promise<void> => {
      if (carePlanEnrollmentId) {
        await refetch();
      }
    },
  };
};
